/** Presentational panels for the live dashboard. */

import { Fragment } from "react";
import type { ReactNode } from "react";
import { Alert, Badge, Card } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";
import type { E911Warning, NodeSnapshot } from "@pbxmgr/api";

const HEALTH_TONE: Record<string, BadgeTone> = {
  online: "online",
  offline: "offline",
  degraded: "degraded",
  unknown: "unknown",
};

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString();
}

function renderCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** A titled section wrapping content in a card. */
export function Panel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="dash-panel">
      <h2>{title}</h2>
      <Card>{children}</Card>
    </section>
  );
}

/** Per-node health with live counts. */
export function NodeHealthTable({
  nodes,
}: {
  nodes: NodeSnapshot[];
}): ReactNode {
  if (nodes.length === 0)
    return <div className="ui-empty">No nodes configured.</div>;
  return (
    <table className="ui-table">
      <thead>
        <tr>
          <th>Node</th>
          <th>Health</th>
          <th>Calls</th>
          <th>Registrations</th>
          <th>Trunks</th>
          <th>Feed</th>
          <th>Last seen</th>
        </tr>
      </thead>
      <tbody>
        {nodes.map((n) => (
          <tr key={n.node_id}>
            <td>{n.name}</td>
            <td>
              <Badge tone={HEALTH_TONE[n.health] ?? "unknown"}>
                {n.health}
              </Badge>
            </td>
            <td>{n.active_call_count}</td>
            <td>{n.registration_count}</td>
            <td>{n.trunk_count}</td>
            <td>{n.feed_connected ? "connected" : "—"}</td>
            <td>{formatTime(n.last_seen)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A generic table whose columns are the union of the rows' keys — the call,
 * registration, and trunk frames come straight from the nodes, so their exact
 * fields vary by node/firmware and are rendered as-is.
 */
export function DataTable({
  rows,
  hide = [],
  detail = [],
}: {
  rows: Array<Record<string, unknown>>;
  hide?: string[];
  /**
   * Columns rendered as a full-width labelled line under the row instead of
   * as a column — for long unbreakable values (a registration's Contact)
   * that would otherwise set the width of the whole table (ADR 0025).
   */
  detail?: string[];
}): ReactNode {
  if (rows.length === 0)
    return <div className="ui-empty">Nothing to show.</div>;
  const all = Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).filter(
    (c) => !hide.includes(c),
  );
  const columns = all.filter((c) => !detail.includes(c));
  const under = all.filter((c) => detail.includes(c));
  const hasDetail = (row: Record<string, unknown>): boolean =>
    under.some((c) => row[c] !== undefined && row[c] !== null && row[c] !== "");
  return (
    <table className="ui-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <Fragment key={i}>
            <tr className={hasDetail(row) ? "ui-table__main-row" : undefined}>
              {columns.map((c) => (
                <td key={c}>{renderCell(row[c])}</td>
              ))}
            </tr>
            {hasDetail(row) ? (
              <tr className="ui-table__detail-row">
                <td colSpan={Math.max(columns.length, 1)}>
                  {under.map((c) => (
                    <div key={c}>
                      <span className="ui-table__detail-label">{c}</span>
                      {renderCell(row[c])}
                    </div>
                  ))}
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** E911 IP spot-check warnings (§4B): a device registering outside its range. */
export function E911Warnings({
  warnings,
}: {
  warnings: E911Warning[];
}): ReactNode {
  if (warnings.length === 0)
    return <div className="ui-empty">No E911 location warnings.</div>;
  return (
    <div>
      {warnings.map((w, i) => (
        <Alert key={i}>
          <strong>{w.label ?? w.mac ?? "device"}</strong>
          {w.extension ? ` (ext ${w.extension})` : ""} registering from{" "}
          {w.registering_ip} — {w.message}
        </Alert>
      ))}
    </div>
  );
}
